// Suggests which information nodes are states of the same information asset, from their names, so linking them doesn't
// rest on someone finding each one by hand. An asset is the information itself (a customer order) and its nodes are the
// stages it passes through (order received, order checked), so names are compared without the words saying which stage.
// Nothing is linked until the user accepts a suggestion.

// a piece of information that can be linked to an asset: an output, or information passed between steps
export interface LinkableInformation {
    key: string;
    name: string;
    modelKey: string;
    model: string;
    assetKey?: string | null;
}

export interface AssetLinkSuggestion {
    id: string;                     // the same for the same suggestion, so a rejected one stays rejected
    kind: 'existing' | 'new';       // link to an asset there is, or create one and link to it
    assetKey?: string;              // the asset there is
    assetName: string;              // its name, or the name for the new one
    nodes: LinkableInformation[];
    models: number;
}

// words saying which stage information is at, or joining others, which say nothing of what the information is
const STAGE_WORDS = new Set([
    'a', 'an', 'the', 'of', 'for', 'to', 'and', 'from', 'with', 'new', 'draft', 'initial', 'final', 'current', 'latest', 'received', 'checked',
    'validated', 'verified', 'approved', 'rejected', 'completed', 'complete', 'updated', 'signed', 'submitted', 'processed', 'confirmed',
    'cancelled', 'canceled', 'closed', 'open', 'pending', 'revised', 'amended', 'filed', 'sent', 'issued', 'paid', 'unpaid', 'copy',
]);

const singular = (word: string) => (word.length > 3 && word.endsWith('s') && !word.endsWith('ss') ? word.slice(0, -1) : word);

// the words naming the information itself, lower case and singular
export const coreWords = (name: string): string[] =>
    (name || '').toLowerCase().split(/[^a-z0-9]+/).filter(w => w && !STAGE_WORDS.has(w)).map(singular);

// a name for a new asset: the first piece of information's name without its stage words, as it was written
const assetNameFor = (name: string) => {
    const words = (name || '').trim().split(/\s+/).filter(w => !STAGE_WORDS.has(w.toLowerCase().replace(/[^a-z0-9]/g, '')));
    const text = words.join(' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
};

const modelCount = (nodes: LinkableInformation[]) => new Set(nodes.map(n => n.modelKey)).size;

/**
 * Suggests links for the information not yet linked to an asset:
 * - to an asset whose name's words all appear in the information's name (the asset Customer order for "Customer order
 *   received"), the asset with the most words where several match
 * - otherwise, where pieces of information in two or more models share the same words, a new asset for them
 */
export const suggestAssetLinks = (information: LinkableInformation[], assets: { key: string; name: string }[]): AssetLinkSuggestion[] => {
    const seen = new Set<string>();
    const unlinked = information.filter(i => !i.assetKey && !seen.has(i.key) && seen.add(i.key));
    const assetWords = assets.map(a => ({ ...a, words: coreWords(a.name) })).filter(a => a.words.length)
        .sort((a, b) => b.words.length - a.words.length || a.name.localeCompare(b.name));

    const toExisting = new Map<string, LinkableInformation[]>();
    const left: LinkableInformation[] = [];
    unlinked.forEach(i => {
        const words = new Set(coreWords(i.name));
        const asset = assetWords.find(a => a.words.every(w => words.has(w)));
        if (asset) toExisting.set(asset.key, [...(toExisting.get(asset.key) || []), i]);
        else left.push(i);
    });

    const alike = new Map<string, LinkableInformation[]>();
    left.forEach(i => {
        const core = [...new Set(coreWords(i.name))].sort().join(' ');
        if (core) alike.set(core, [...(alike.get(core) || []), i]);
    });

    const byName = (a: LinkableInformation, b: LinkableInformation) => a.model.localeCompare(b.model) || a.name.localeCompare(b.name);
    const idOf = (kind: string, of: string, nodes: LinkableInformation[]) => `${kind}:${of}:${nodes.map(n => n.key).sort().join(',')}`;
    const existing: AssetLinkSuggestion[] = assetWords.filter(a => toExisting.has(a.key)).map(a => {
        const nodes = toExisting.get(a.key)!.sort(byName);
        return { id: idOf('existing', a.key, nodes), kind: 'existing', assetKey: a.key, assetName: a.name, nodes, models: modelCount(nodes) };
    });
    const created: AssetLinkSuggestion[] = [...alike.entries()]
        .filter(([, nodes]) => modelCount(nodes) > 1)
        .map(([core, nodes]) => {
            const sorted = nodes.sort(byName);
            return { id: idOf('new', core, sorted), kind: 'new' as const, assetName: assetNameFor(sorted[0].name), nodes: sorted, models: modelCount(sorted) };
        })
        // a new asset needs a name long enough to be one
        .filter(s => s.assetName.length >= 3);

    // the suggestions linking the most information first
    return [...existing, ...created].sort((a, b) => b.nodes.length - a.nodes.length || b.models - a.models || a.assetName.localeCompare(b.assetName));
};
